/**
 * กำไรจากการตรวจนับสต๊อก -- how much the shop earned at each stock count.
 *
 * A consignment sale never touches stock at the till. The shop learns
 * what sold only when it counts: every piece missing since the previous
 * count was sold, at that lot's price. So "the profit of a count day" is
 * the profit of everything sold between the previous count and that one,
 * and it is read straight from the STOCK_CHECK history rows Backend wrote.
 *
 * WHICH FIELDS, AND WHY NOT `shop_profit`
 * ----------------------------------------------------------------------
 * A STOCK_CHECK row's `shop_profit`, `sales_amount` and `calculated_sold`
 * are the lot's running CUMULATIVE totals after that count, not what that
 * count added. Summing them across two counts of one lot counts the first
 * sale twice. What a count added is in `quantity_change` (negative =
 * pieces sold, FIFO-attributed to this lot by Backend) at the row's own
 * `selling_price_per_unit` / `owner_cost_per_unit`, exactly the figures
 * Backend's `_simulate_stock_check` used. Summed over every count of a lot
 * this equals the lot's stored `shop_profit` -- checked against all 25
 * Production lots on 2026-10-08 with no mismatch.
 *
 * A positive `quantity_change` is a count that found MORE than the system
 * expected: Backend reverses that many earlier "sold" pieces (newest lot
 * first), so it enters here as a negative sale, keeping the totals net.
 *
 * Money is added in whole satang so 0.1 + 0.2 never appears on a screen.
 */
import { Consignment, ConsignmentHistory } from '../../model/consignment';
import { formatActor } from '../../utils/audit-format';

export type CountProfitMode = 'today' | 'month' | 'year' | 'custom';

/** Inclusive calendar range, both ends `YYYY-MM-DD` in shop time. */
export interface CountProfitRange {
    start: string;
    end: string;
}

export interface CountProfitBillInput {
    bill: Consignment;
    history: ConsignmentHistory[];
}

export interface CountProfitProductRow {
    key: string;
    product_name: string;
    product_code?: string;
    product_unit_name?: string;
    vendor_name?: string;
    document_nos: string[];
    lot_count: number;
    /** Net pieces sold: sold minus any count that found more than expected. */
    sold_quantity: number;
    /** Pieces a later count put back (counted more than the system had). */
    corrected_quantity: number;
    selling_price_per_unit: number;
    owner_cost_per_unit: number;
    unit_profit: number;
    sales_amount: number;
    owner_payable: number;
    shop_profit: number;
}

export interface CountProfitSessionRow {
    key: string;
    consignment_id: number;
    document_no: string;
    vendor_name?: string;
    /** `YYYY-MM-DD HH:mm:ss`, shop time, of the count's first row. */
    counted_at: string;
    recorded_by: string;
    products_counted: number;
    sold_quantity: number;
    corrected_quantity: number;
    sales_amount: number;
    owner_payable: number;
    shop_profit: number;
}

export interface CountProfitTotals {
    count_sessions: number;
    bill_count: number;
    sold_quantity: number;
    corrected_quantity: number;
    sales_amount: number;
    owner_payable: number;
    shop_profit: number;
}

export interface CountProfitReport {
    products: CountProfitProductRow[];
    sessions: CountProfitSessionRow[];
    totals: CountProfitTotals;
    /** STOCK_CHECK rows whose time could not be read. Never guessed into a
     * day; the screen says how many were left out. */
    unreadable_rows: number;
}

const SHOP_TIME_ZONE = 'Asia/Bangkok';

/** Two saves of one bill further apart than this are two counts. Rows of
 * one save are written in one transaction, a second or so apart. */
const SESSION_GAP_SECONDS = 120;

const NAIVE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
/** An ISO instant with its offset stated -- the only shape safe to give
 * `new Date()`. Anything else ("08-10-2026 12:00") an engine would happily
 * read as August the 10th. */
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n: number) => String(n).padStart(2, '0');

const shopClockParts = (date: Date): string | null => {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: SHOP_TIME_ZONE,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        }).formatToParts(date);
        const find = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
        const hour = find('hour') === '24' ? '00' : find('hour');
        const value = `${find('year')}-${find('month')}-${find('day')} ${hour}:${find('minute')}:${find('second')}`;
        return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? value : null;
    } catch (e) {
        return null;
    }
};

/**
 * A history timestamp as shop wall clock, `YYYY-MM-DD HH:mm:ss`.
 *
 * Same rule as `formatEventTime`: `created_at_iso` carries an offset and is
 * converted to Bangkok; the legacy naive `created_at` is already Bangkok
 * wall clock and is read digit for digit, never handed to `new Date()`.
 */
export const shopWallClock = (value?: string | null): string | null => {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    const naive = NAIVE.exec(raw);
    if (naive) {
        const [, y, mo, d, h, mi, s] = naive;
        return `${y}-${mo}-${d} ${h}:${mi}:${s ?? '00'}`;
    }
    if (!ISO_WITH_OFFSET.test(raw)) return null;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return shopClockParts(parsed);
};

const rowWallClock = (row: ConsignmentHistory): string | null =>
    shopWallClock(row.created_at_iso) ?? shopWallClock(row.created_at);

/** Today's date in the shop, whatever the viewing machine's timezone. */
export const shopToday = (now: Date = new Date()): string => {
    const clock = shopClockParts(now);
    return clock ? clock.slice(0, 10) : `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const lastDayOfMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The calendar range a choice covers. `today` is a shop date. */
export const countProfitRange = (mode: CountProfitMode, today: string, custom?: CountProfitRange | null): CountProfitRange => {
    const [y, m] = today.split('-').map(Number);
    switch (mode) {
        case 'month':
            return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastDayOfMonth(y, m))}` };
        case 'year':
            return { start: `${y}-01-01`, end: `${y}-12-31` };
        case 'custom':
            if (custom && DATE_ONLY.test(custom.start) && DATE_ONLY.test(custom.end)) {
                return custom.start <= custom.end ? custom : { start: custom.end, end: custom.start };
            }
            return { start: today, end: today };
        case 'today':
        default:
            return { start: today, end: today };
    }
};

const displayDate = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}`;

export const formatCountProfitRange = (range: CountProfitRange): string =>
    range.start === range.end ? displayDate(range.start) : `${displayDate(range.start)} – ${displayDate(range.end)}`;

/**
 * Whether a bill could hold a count inside the range, so its history is
 * worth fetching. Only a stock check writes the header's `last_checked_at`,
 * and nothing ever moves it backwards, so a bill last counted before the
 * range starts has no count in it. Anything unreadable is fetched anyway:
 * a wasted request is cheaper than a count silently missing from a total.
 */
export const billMayHaveCountsIn = (bill: Consignment, range: CountProfitRange): boolean => {
    const day = bill.last_checked_at ? String(bill.last_checked_at).slice(0, 10) : '';
    if (!DATE_ONLY.test(day)) return true;
    return day >= range.start;
};

const satang = (value: unknown) => Math.round(Number(value || 0) * 100);
const baht = (value: number) => value / 100;

const secondsBetween = (a: string, b: string) => {
    const toSeconds = (v: string) => Date.UTC(
        Number(v.slice(0, 4)), Number(v.slice(5, 7)) - 1, Number(v.slice(8, 10)),
        Number(v.slice(11, 13)), Number(v.slice(14, 16)), Number(v.slice(17, 19))
    ) / 1000;
    return Math.abs(toSeconds(b) - toSeconds(a));
};

interface DatedRow { row: ConsignmentHistory; at: string }

/**
 * Splits one bill's history into counts. A count is a run of STOCK_CHECK
 * rows with no other movement between them and no long pause: one save
 * writes all of its rows together, so anything else in between, or a gap
 * of minutes, means the shop pressed save again.
 */
const splitIntoCounts = (history: ConsignmentHistory[]): { counts: DatedRow[][]; unreadable: number } => {
    const ordered = [...history].sort((a, b) => Number(a.id) - Number(b.id));
    const counts: DatedRow[][] = [];
    let current: DatedRow[] | null = null;
    let unreadable = 0;
    for (const row of ordered) {
        // Only STOCK_CHECK carries a sale. The pre-V2 `CHECK` rows used a
        // different ledger shape and Production has none.
        if (row.action_type !== 'STOCK_CHECK') {
            current = null;
            continue;
        }
        const at = rowWallClock(row);
        if (!at) {
            unreadable += 1;
            current = null;
            continue;
        }
        const previous = current ? current[current.length - 1] : null;
        if (!current || !previous || secondsBetween(previous.at, at) > SESSION_GAP_SECONDS) {
            current = [];
            counts.push(current);
        }
        current.push({ row, at });
    }
    return { counts, unreadable };
};

export const buildCountProfitReport = (bills: CountProfitBillInput[], range: CountProfitRange): CountProfitReport => {
    const products = new Map<string, CountProfitProductRow & { lots: Set<string>; docs: Set<string>; s: number; p: number; pr: number }>();
    const sessions: CountProfitSessionRow[] = [];
    const billsWithCounts = new Set<number>();
    let unreadable = 0;

    for (const { bill, history } of bills) {
        const split = splitIntoCounts(Array.isArray(history) ? history : []);
        for (const count of split.counts) {
            const counted_at = count[0].at;
            const day = counted_at.slice(0, 10);
            // A count belongs wholly to the day it started on.
            if (day < range.start || day > range.end) continue;

            let sold = 0, corrected = 0, sales = 0, payable = 0;
            const productsCounted = new Set<string>();
            for (const { row } of count) {
                const productKey = `${row.product_code ?? ''}|${row.product_name ?? ''}`;
                productsCounted.add(productKey);
                const change = Math.round(Number(row.quantity_change || 0));
                if (change === 0) continue;
                const pieces = -change;
                const sell = satang(row.selling_price_per_unit);
                const cost = satang(row.owner_cost_per_unit);
                if (pieces > 0) sold += pieces; else corrected += -pieces;
                sales += pieces * sell;
                payable += pieces * cost;

                const key = `${bill.vendor_name ?? ''}|${productKey}|${sell}|${cost}`;
                let line = products.get(key);
                if (!line) {
                    line = {
                        key,
                        product_name: row.product_name || '-',
                        product_code: row.product_code,
                        product_unit_name: row.product_unit_name,
                        vendor_name: bill.vendor_name,
                        document_nos: [], lot_count: 0,
                        sold_quantity: 0, corrected_quantity: 0,
                        selling_price_per_unit: baht(sell), owner_cost_per_unit: baht(cost),
                        unit_profit: baht(sell - cost),
                        sales_amount: 0, owner_payable: 0, shop_profit: 0,
                        lots: new Set(), docs: new Set(), s: 0, p: 0, pr: 0,
                    };
                    products.set(key, line);
                }
                line.sold_quantity += pieces;
                if (pieces < 0) line.corrected_quantity += -pieces;
                line.s += pieces * sell;
                line.p += pieces * cost;
                line.lots.add(String(row.consignment_item_id ?? row.batch_no ?? row.id));
                line.docs.add(bill.document_no);
            }

            billsWithCounts.add(bill.id);
            sessions.push({
                key: `${bill.id}-${count[0].row.id}`,
                consignment_id: bill.id,
                document_no: bill.document_no,
                vendor_name: bill.vendor_name,
                counted_at,
                recorded_by: formatActor(count[0].row.recorded_by_name),
                products_counted: productsCounted.size,
                // Net, like every other figure: the money is net of both.
                sold_quantity: sold - corrected,
                corrected_quantity: corrected,
                sales_amount: baht(sales),
                owner_payable: baht(payable),
                shop_profit: baht(sales - payable),
            });
        }
        unreadable += split.unreadable;
    }

    const productRows: CountProfitProductRow[] = Array.from(products.values()).map(({ lots, docs, s, p, ...line }) => ({
        ...line,
        lot_count: lots.size,
        document_nos: Array.from(docs).sort(),
        sales_amount: baht(s),
        owner_payable: baht(p),
        shop_profit: baht(s - p),
    }));
    productRows.sort((a, b) => b.shop_profit - a.shop_profit || a.product_name.localeCompare(b.product_name, 'th'));
    sessions.sort((a, b) => (a.counted_at < b.counted_at ? 1 : a.counted_at > b.counted_at ? -1 : 0));

    const sum = (pick: (s: CountProfitSessionRow) => number) => sessions.reduce((acc, s) => acc + Math.round(pick(s) * 100), 0);
    const sales = sum((s) => s.sales_amount);
    const payable = sum((s) => s.owner_payable);
    return {
        products: productRows,
        sessions,
        totals: {
            count_sessions: sessions.length,
            bill_count: billsWithCounts.size,
            sold_quantity: sessions.reduce((acc, s) => acc + s.sold_quantity, 0),
            corrected_quantity: sessions.reduce((acc, s) => acc + s.corrected_quantity, 0),
            sales_amount: baht(sales),
            owner_payable: baht(payable),
            shop_profit: baht(sales - payable),
        },
        unreadable_rows: unreadable,
    };
};
